"use client";
import { forwardRef, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "react-quill-new/dist/quill.snow.css";
import "quill-table-better/dist/quill-table-better.css";
import ReactQuillComponent, { Quill } from "react-quill-new";
import { formatsCourseAbout } from "./const/formatsCourseAbout";
import { formatsReactQuill } from "./const/formatsReactQuill";
import useReactQuillSimple, { ReactQuillToolbarPreset, WebpOptimizionType } from "./model/useReactQuillSimple";
import { htmlHasTable, setQuillHtml } from "./model/setQuillHtml";
import ImageScaleField from "./ImageScaleField";
import ReactQuillSpinner from "./ReactQuillSpinner";
import { QuillImageUploadFunction, QuillSelectKeyHandler } from "./types";

type QuillInstance = InstanceType<typeof ReactQuillComponent>;

interface QuillEditorProps {
  readOnly?: boolean;
  theme?: string;
  modules?: Record<string, unknown>;
  formats?: string[];
  value?: string;
  useSemanticHTML?: boolean;
  onChange?: (value: string, delta: unknown, source: string) => void;
}

const QuillNoSSR = ReactQuillComponent as unknown as React.ComponentType<
  QuillEditorProps & { ref?: React.Ref<QuillInstance> }
>;

const ReactQuillWithRef = forwardRef<QuillInstance, QuillEditorProps>((props, ref) => (
  <QuillNoSSR ref={ref} {...props} />
));
ReactQuillWithRef.displayName = "ReactQuillWithRef";
 
interface Props {
  value: string;
  onChange: (value: string) => void;
  webpOptimizion?: WebpOptimizionType;
  uploadImageFunction?: QuillImageUploadFunction;
  uploadPastedImage?: QuillImageUploadFunction;
  /** courseAbout — лише h2–h4, b/i/u, маркований список */
  toolbarPreset?: ReactQuillToolbarPreset;
  readOnly?: boolean;
  disabled?: boolean;
  autoGrow?: boolean;
  stickyMode?:boolean;
  stickyTop?:number;
  /** When set, shows "Insert key" toolbar button before paste. */
  onSelectKey?: QuillSelectKeyHandler;
}

export default function ReactQuillSimple({
  value,
  onChange,
  webpOptimizion,
  uploadImageFunction,
  uploadPastedImage,
  toolbarPreset = "default",
  readOnly = false,
  disabled = false,
  autoGrow = false,
  stickyMode = true,
  stickyTop = 0,
  onSelectKey,
}: Props) {
  const { t } = useTranslation("common");
  const { attachRef, modules, isBusy, editor, getSafeEditor } = useReactQuillSimple(
    webpOptimizion,
    uploadImageFunction,
    uploadPastedImage,
    toolbarPreset,
    onSelectKey,
    {
      pasteFromClipboard: t("clipboard.paste"),
      insertTemplateKey: t("clipboard.insertKey"),
    },
  );
  const formats = toolbarPreset === "courseAbout" ? formatsCourseAbout : formatsReactQuill;
  const readOnlyEffective = readOnly || disabled;
  const tableLoaderEnabledRef = useRef(false);
  tableLoaderEnabledRef.current = toolbarPreset !== "courseAbout";

  // react-quill-new loads `value` via setContents, which breaks quill-table-better.
  // After the editor is ready, re-apply HTML that contains tables via updateContents.
  const tableSyncRef = useRef<{ editor: Quill | null; html: string | null }>({
    editor: null,
    html: null,
  });

  useEffect(() => {
    if (!editor || toolbarPreset === "courseAbout") return;
    const html = value || "";
    if (!htmlHasTable(html)) return;
    if (tableSyncRef.current.editor === editor && tableSyncRef.current.html === html) return;
    setQuillHtml(editor, html);
    tableSyncRef.current = { editor, html };
  }, [editor, toolbarPreset, value]);

  const patchTableValueLoader = useCallback((instance: QuillInstance | null) => {
    if (!instance) return;

    const reactQuill = instance as unknown as {
      __tableValueLoaderPatched?: boolean;
      value?: unknown;
      setEditorContents?: (editor: Quill, value: unknown) => void;
    };
    if (reactQuill.__tableValueLoaderPatched || typeof reactQuill.setEditorContents !== "function") {
      return;
    }

    const setEditorContents = reactQuill.setEditorContents.bind(instance);
    reactQuill.setEditorContents = (quill: Quill, nextValue: unknown) => {
      if (
        tableLoaderEnabledRef.current &&
        typeof nextValue === "string" &&
        htmlHasTable(nextValue)
      ) {
        reactQuill.value = nextValue;
        setQuillHtml(quill, nextValue);
        return;
      }

      setEditorContents(quill, nextValue);
    };
    reactQuill.__tableValueLoaderPatched = true;
  }, []);

  const handleQuillRef = useCallback(
    (instance: QuillInstance | null) => {
      patchTableValueLoader(instance);
      attachRef(instance);
    },
    [attachRef, patchTableValueLoader],
  );

  return (
    <div
      className={`react-quill-match-inputs ql-editor-live position-relative rounded-4 border- border p-2 ${stickyMode ? "quill-sticky" : ""} ${autoGrow ? "quill-autogrow" : ""}`}
    >
    <style>{`
      .react-quill-match-inputs {
        --editor-toolbar-bg: #ffffff;
        --editor-toolbar-border: rgba(54, 87, 93, 0.22);
      }
      [data-bs-theme="dark"] .react-quill-match-inputs,
      [data-pc-theme="dark"] .react-quill-match-inputs {
        --editor-toolbar-bg: #1f2a37;
        --editor-toolbar-border: rgba(255, 255, 255, 0.2);
      }
      .react-quill-match-inputs .ql-toolbar.ql-snow {
        background-color: var(--editor-toolbar-bg) !important;
        border: 1px solid var(--editor-toolbar-border) !important;
        /* Concentric with parent rounded-4 (1rem) minus p-2 (0.5rem) */
        border-radius: calc(var(--bs-border-radius-xl, 1rem) - 0.5rem) !important;
        opacity: 1 !important;
        backdrop-filter: none !important;
        margin-bottom: 0.5rem;
        padding: 8px 6px !important;
        box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.06) !important;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0;
      }
      .react-quill-match-inputs .ql-toolbar.ql-snow .ql-formats {
        display: inline-flex !important;
        align-items: center;
        vertical-align: middle;
        margin-bottom: 0 !important;
      }
      .react-quill-match-inputs .ql-toolbar.ql-snow button,
      .react-quill-match-inputs .ql-toolbar.ql-snow .ql-picker {
        float: none !important;
        vertical-align: middle;
      }
      .react-quill-match-inputs .ql-toolbar.ql-snow .ql-color-picker .ql-picker-label,
      .react-quill-match-inputs .ql-toolbar.ql-snow .ql-icon-picker .ql-picker-label {
        display: inline-flex !important;
        align-items: center;
        justify-content: center;
        height: 24px;
        padding: 0 4px !important;
        line-height: 1;
        overflow: visible;
      }
      /* Safari: viewBox-only SVGs collapse without explicit size after position:static */
      .react-quill-match-inputs .ql-toolbar.ql-snow .ql-color-picker .ql-picker-label svg,
      .react-quill-match-inputs .ql-toolbar.ql-snow .ql-icon-picker .ql-picker-label svg {
        position: static !important;
        display: block !important;
        width: 18px !important;
        height: 18px !important;
        flex-shrink: 0;
        margin: 0 !important;
        right: auto !important;
        top: auto !important;
        overflow: visible;
      }
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow,
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow {
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35), 0 1px 2px rgba(0, 0, 0, 0.25) !important;
      }
      .react-quill-match-inputs .ql-container.ql-snow {
        border: none !important;
      }
      /* Unify toolbar icons: thinner strokes (like image), blue hover */
      .react-quill-match-inputs .ql-toolbar.ql-snow .ql-stroke,
      .react-quill-match-inputs .ql-toolbar.ql-snow .ql-stroke-miter {
        stroke-width: 1.15 !important;
        fill: none !important;
      }
      .react-quill-match-inputs .ql-toolbar.ql-snow .ql-thin,
      .react-quill-match-inputs .ql-toolbar.ql-snow .ql-stroke.ql-thin {
        stroke-width: 1 !important;
      }
      .react-quill-match-inputs .ql-toolbar.ql-snow button:hover,
      .react-quill-match-inputs .ql-toolbar.ql-snow button:focus,
      .react-quill-match-inputs .ql-toolbar.ql-snow button.ql-active,
      .react-quill-match-inputs .ql-toolbar.ql-snow .ql-picker-label:hover,
      .react-quill-match-inputs .ql-toolbar.ql-snow .ql-picker-label.ql-active {
        color: var(--bs-primary, #4680ff) !important;
      }
      .react-quill-match-inputs .ql-toolbar.ql-snow button:hover .ql-stroke,
      .react-quill-match-inputs .ql-toolbar.ql-snow button:focus .ql-stroke,
      .react-quill-match-inputs .ql-toolbar.ql-snow button.ql-active .ql-stroke,
      .react-quill-match-inputs .ql-toolbar.ql-snow .ql-picker-label:hover .ql-stroke,
      .react-quill-match-inputs .ql-toolbar.ql-snow .ql-picker-label.ql-active .ql-stroke,
      .react-quill-match-inputs .ql-toolbar.ql-snow button:hover .ql-stroke-miter,
      .react-quill-match-inputs .ql-toolbar.ql-snow button:focus .ql-stroke-miter,
      .react-quill-match-inputs .ql-toolbar.ql-snow button.ql-active .ql-stroke-miter {
        stroke: var(--bs-primary, #4680ff) !important;
      }
      .react-quill-match-inputs .ql-toolbar.ql-snow button:hover .ql-fill,
      .react-quill-match-inputs .ql-toolbar.ql-snow button:focus .ql-fill,
      .react-quill-match-inputs .ql-toolbar.ql-snow button.ql-active .ql-fill,
      .react-quill-match-inputs .ql-toolbar.ql-snow .ql-picker-label:hover .ql-fill,
      .react-quill-match-inputs .ql-toolbar.ql-snow .ql-picker-label.ql-active .ql-fill,
      .react-quill-match-inputs .ql-toolbar.ql-snow button:hover .ql-stroke.ql-fill,
      .react-quill-match-inputs .ql-toolbar.ql-snow button.ql-active .ql-stroke.ql-fill {
        fill: var(--bs-primary, #4680ff) !important;
      }
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow button,
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow button,
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow .ql-picker-label,
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow .ql-picker-label {
        color: #94a3b8 !important;
      }
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow .ql-stroke,
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow .ql-stroke,
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow .ql-stroke-miter,
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow .ql-stroke-miter {
        stroke: #94a3b8 !important;
        fill: none !important;
      }
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow .ql-fill,
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow .ql-fill {
        fill: #94a3b8 !important;
      }
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow button:hover,
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow button:hover,
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow button:focus,
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow button:focus,
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow button.ql-active,
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow button.ql-active,
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow .ql-picker-label:hover,
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow .ql-picker-label:hover {
        color: var(--bs-primary, #4680ff) !important;
      }
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow button:hover .ql-stroke,
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow button:hover .ql-stroke,
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow button:focus .ql-stroke,
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow button:focus .ql-stroke,
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow button.ql-active .ql-stroke,
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow button.ql-active .ql-stroke,
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow .ql-picker-label:hover .ql-stroke,
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow .ql-picker-label:hover .ql-stroke {
        stroke: var(--bs-primary, #4680ff) !important;
      }
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow button:hover .ql-fill,
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow button:hover .ql-fill,
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow button:focus .ql-fill,
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow button:focus .ql-fill,
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow button.ql-active .ql-fill,
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-toolbar.ql-snow button.ql-active .ql-fill {
        fill: var(--bs-primary, #4680ff) !important;
      }
      .react-quill-match-inputs .ql-toolbar.ql-snow button.ql-table-better svg {
        width: 18px !important;
        height: 18px !important;
      }
      .react-quill-match-inputs .ql-toolbar.ql-snow button.ql-table-better svg path {
        fill: currentColor !important;
        stroke: none !important;
      }
      .react-quill-match-inputs .ql-toolbar.ql-snow button.ql-table-better svg .ql-stroke {
        fill: none !important;
        stroke: currentColor !important;
      }
      /* Restore quill-table-better focused/selected cell visuals in live editor only */
      .ql-editor-live td.ql-cell-focused {
        position: relative !important;
      }
      .ql-editor-live td.ql-cell-focused::after,
      .ql-editor-live td.ql-cell-selected::after,
      .ql-editor-live .ql-cell-selected-after {
        content: "" !important;
        display: block !important;
        pointer-events: none;
        position: absolute;
        inset: 0;
        background-color: rgba(158, 207, 250, 0.3) !important;
        border: none !important;
      }
      [data-bs-theme="dark"] .ql-table-menus-container,
      [data-pc-theme="dark"] .ql-table-menus-container {
        background: #1f2a37 !important;
        border-color: rgba(255, 255, 255, 0.22) !important;
        opacity: 1 !important;
        z-index: 30 !important;
      }
      .ql-table-menus-container {
        z-index: 30 !important;
      }
      .ql-table-dropdown-list,
      .ql-table-dropdown-properties-list,
      .ql-table-properties-form,
      .ql-table-select-container {
        z-index: 40 !important;
      }
      [data-bs-theme="dark"] .ql-table-menus-container .ql-table-tooltip-hover,
      [data-pc-theme="dark"] .ql-table-menus-container .ql-table-tooltip-hover {
        color: #e2e8f0 !important;
        background: transparent !important;
        opacity: 1 !important;
        border-radius: 4px;
      }
      [data-bs-theme="dark"] .ql-table-menus-container .ql-table-tooltip-hover:hover,
      [data-pc-theme="dark"] .ql-table-menus-container .ql-table-tooltip-hover:hover {
        background: rgba(255, 255, 255, 0.12) !important;
      }
      [data-bs-theme="dark"] .ql-table-menus-container .ql-table-tooltip-hover.ql-table-selected,
      [data-pc-theme="dark"] .ql-table-menus-container .ql-table-tooltip-hover.ql-table-selected {
        background: rgba(96, 165, 250, 0.22) !important;
        box-shadow: inset 0 0 0 1px rgba(96, 165, 250, 0.65) !important;
      }
      /* Plugin: .ql-table-dropdown:hover { background:#f0f0f0 } — white-on-white icons */
      [data-bs-theme="dark"] .ql-table-dropdown,
      [data-pc-theme="dark"] .ql-table-dropdown,
      [data-bs-theme="dark"] .ql-table-dropdown-properties,
      [data-pc-theme="dark"] .ql-table-dropdown-properties {
        background: transparent !important;
        color: #e2e8f0 !important;
      }
      [data-bs-theme="dark"] .ql-table-dropdown:hover,
      [data-pc-theme="dark"] .ql-table-dropdown:hover,
      [data-bs-theme="dark"] .ql-table-dropdown-properties:hover,
      [data-pc-theme="dark"] .ql-table-dropdown-properties:hover {
        background: rgba(255, 255, 255, 0.12) !important;
        color: #e2e8f0 !important;
      }
      /* Plugin icons use hardcoded stroke="#333" — force light strokes in dark theme */
      [data-bs-theme="dark"] .ql-table-menus-container svg,
      [data-pc-theme="dark"] .ql-table-menus-container svg,
      [data-bs-theme="dark"] .ql-table-dropdown svg,
      [data-pc-theme="dark"] .ql-table-dropdown svg,
      [data-bs-theme="dark"] .ql-table-dropdown-properties svg,
      [data-pc-theme="dark"] .ql-table-dropdown-properties svg {
        color: #e2e8f0 !important;
        opacity: 1 !important;
      }
      [data-bs-theme="dark"] .ql-table-menus-container svg path,
      [data-pc-theme="dark"] .ql-table-menus-container svg path,
      [data-bs-theme="dark"] .ql-table-menus-container svg line,
      [data-pc-theme="dark"] .ql-table-menus-container svg line,
      [data-bs-theme="dark"] .ql-table-menus-container svg polyline,
      [data-pc-theme="dark"] .ql-table-menus-container svg polyline,
      [data-bs-theme="dark"] .ql-table-menus-container svg rect,
      [data-pc-theme="dark"] .ql-table-menus-container svg rect,
      [data-bs-theme="dark"] .ql-table-menus-container svg circle,
      [data-pc-theme="dark"] .ql-table-menus-container svg circle,
      [data-bs-theme="dark"] .ql-table-dropdown svg path,
      [data-pc-theme="dark"] .ql-table-dropdown svg path,
      [data-bs-theme="dark"] .ql-table-dropdown svg line,
      [data-pc-theme="dark"] .ql-table-dropdown svg line,
      [data-bs-theme="dark"] .ql-table-dropdown-properties svg path,
      [data-pc-theme="dark"] .ql-table-dropdown-properties svg path {
        stroke: #e2e8f0 !important;
      }
      [data-bs-theme="dark"] .ql-table-menus-container svg path[fill]:not([fill="none"]),
      [data-pc-theme="dark"] .ql-table-menus-container svg path[fill]:not([fill="none"]),
      [data-bs-theme="dark"] .ql-table-menus-container svg rect[fill]:not([fill="none"]),
      [data-pc-theme="dark"] .ql-table-menus-container svg rect[fill]:not([fill="none"]) {
        fill: #e2e8f0 !important;
      }
      [data-bs-theme="dark"] .ql-table-menus-container .ql-table-button-disabled,
      [data-pc-theme="dark"] .ql-table-menus-container .ql-table-button-disabled {
        opacity: 0.45 !important;
      }
      [data-bs-theme="dark"] .ql-table-menus-container .ql-table-button-disabled svg path,
      [data-pc-theme="dark"] .ql-table-menus-container .ql-table-button-disabled svg path,
      [data-bs-theme="dark"] .ql-table-menus-container .ql-table-button-disabled svg line,
      [data-pc-theme="dark"] .ql-table-menus-container .ql-table-button-disabled svg line {
        stroke: #94a3b8 !important;
      }
      .ql-snow .ql-toolbar button.ql-pasteFromClipboard,
      .ql-snow.ql-toolbar button.ql-pasteFromClipboard,
      .ql-snow .ql-toolbar button.ql-insertTemplateKey,
      .ql-snow.ql-toolbar button.ql-insertTemplateKey {
        width: 28px;
        height: 24px;
        padding: 3px 5px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: var(--bs-body-color, #212529);
      }
      .ql-snow .ql-toolbar button.ql-pasteFromClipboard::before,
      .ql-snow.ql-toolbar button.ql-pasteFromClipboard::before {
        content: "";
        width: 16px;
        height: 16px;
        display: block;
        background-color: currentColor;
        -webkit-mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 18 18'><rect x='4' y='3' width='10' height='12' fill='none' stroke='black' stroke-width='1.4'/><rect x='6' y='1' width='6' height='2' fill='none' stroke='black' stroke-width='1.4'/><path d='M7 9h4M7 11h4' stroke='black' stroke-width='1.4' stroke-linecap='round'/></svg>") center / contain no-repeat;
        mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 18 18'><rect x='4' y='3' width='10' height='12' fill='none' stroke='black' stroke-width='1.4'/><rect x='6' y='1' width='6' height='2' fill='none' stroke='black' stroke-width='1.4'/><path d='M7 9h4M7 11h4' stroke='black' stroke-width='1.4' stroke-linecap='round'/></svg>") center / contain no-repeat;
      }
      .ql-snow .ql-toolbar button.ql-insertTemplateKey::before,
      .ql-snow.ql-toolbar button.ql-insertTemplateKey::before {
        content: "";
        width: 16px;
        height: 16px;
        display: block;
        background-color: currentColor;
        -webkit-mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 18 18'><rect x='3' y='3' width='12' height='12' rx='1.5' fill='none' stroke='black' stroke-width='1.4'/><path d='M6.5 9h5M9 6.5v5' stroke='black' stroke-width='1.4' stroke-linecap='round'/></svg>") center / contain no-repeat;
        mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 18 18'><rect x='3' y='3' width='12' height='12' rx='1.5' fill='none' stroke='black' stroke-width='1.4'/><path d='M6.5 9h5M9 6.5v5' stroke='black' stroke-width='1.4' stroke-linecap='round'/></svg>") center / contain no-repeat;
      }
      .ql-snow .ql-toolbar button.ql-pasteFromClipboard.ql-active,
      .ql-snow.ql-toolbar button.ql-pasteFromClipboard.ql-active,
      .ql-snow .ql-toolbar button.ql-pasteFromClipboard:hover,
      .ql-snow.ql-toolbar button.ql-pasteFromClipboard:hover,
      .ql-snow .ql-toolbar button.ql-pasteFromClipboard:focus,
      .ql-snow.ql-toolbar button.ql-pasteFromClipboard:focus,
      .ql-snow .ql-toolbar button.ql-insertTemplateKey.ql-active,
      .ql-snow.ql-toolbar button.ql-insertTemplateKey.ql-active,
      .ql-snow .ql-toolbar button.ql-insertTemplateKey:hover,
      .ql-snow.ql-toolbar button.ql-insertTemplateKey:hover,
      .ql-snow .ql-toolbar button.ql-insertTemplateKey:focus,
      .ql-snow.ql-toolbar button.ql-insertTemplateKey:focus {
        color: var(--bs-primary, #4680ff);
      }
      /* Height-limited mode: Quill default is .quill{height:200px} + .ql-container{height:100%}
         with toolbar as sibling — Safari paints overflowing images outside the box.
         Flex + min-height:0 keeps scroll clipped inside .ql-editor. */
      .react-quill-match-inputs:not(.quill-autogrow) .quill {
        display: flex !important;
        flex-direction: column;
        width: 100%;
        height: 200px;
        overflow: hidden;
      }
      .react-quill-match-inputs:not(.quill-autogrow) .ql-toolbar.ql-snow {
        flex: 0 0 auto;
      }
      .react-quill-match-inputs:not(.quill-autogrow) .ql-container,
      .react-quill-match-inputs:not(.quill-autogrow) .ql-container.ql-snow {
        position: relative;
        flex: 1 1 auto;
        min-height: 0;
        height: auto !important;
        overflow: hidden;
      }
      .react-quill-match-inputs:not(.quill-autogrow) .ql-editor {
        position: absolute;
        inset: 0;
        height: auto !important;
        max-height: none;
        overflow-x: hidden;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
      }
      .react-quill-match-inputs .ql-editor img {
        max-width: 100%;
        height: auto;
        cursor: pointer;
      }
      /* Same chrome as quill-table-better .ql-table-menus-container / .ql-table-input */
      .editor-image-scale {
        position: fixed;
        z-index: 2000;
        display: flex;
        align-items: center;
        width: fit-content;
        height: 40px;
        padding: 4px;
        box-sizing: border-box;
        gap: 2px;
        overflow: hidden;
        border-radius: 8px;
        border: 1px solid #bec8d0;
        background: #fff;
        box-shadow: 0 4px 16px rgba(15, 23, 42, 0.12);
        color: #333;
      }
      .editor-image-scale__label {
        padding: 0 4px 0 6px;
        font-size: 13px;
        line-height: 30px;
        white-space: nowrap;
        color: inherit;
      }
      .editor-image-scale__input {
        width: 64px;
        height: 30px;
        margin: 0;
        padding: 0 6px;
        box-sizing: border-box;
        border: 1px solid #ccced1;
        border-radius: 6px;
        outline: none;
        background: inherit;
        color: inherit;
        font-size: 14px;
        -moz-appearance: textfield;
        appearance: textfield;
      }
      .editor-image-scale__input::-webkit-outer-spin-button,
      .editor-image-scale__input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      .editor-image-scale__input:focus {
        border: 1px solid #3779eb;
        box-shadow: 0 0 0 3px #cae1fc;
      }
      .editor-image-scale__unit {
        font-size: 13px;
        line-height: 30px;
        margin-right: 4px;
      }
      .editor-image-scale__btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        padding: 0;
        border: none;
        border-radius: 6px;
        background: transparent;
        cursor: pointer;
      }
      .editor-image-scale__btn svg {
        display: block;
        width: 16px;
        height: 16px;
      }
      .editor-image-scale__btn:hover:not(:disabled) {
        background: #f0f0f0;
      }
      .editor-image-scale__btn:disabled {
        opacity: 0.45;
        cursor: default;
      }
      [data-bs-theme="dark"] .editor-image-scale,
      [data-pc-theme="dark"] .editor-image-scale {
        background: #1f2a37;
        border-color: rgba(255, 255, 255, 0.22);
        color: #e2e8f0;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.45);
      }
      [data-bs-theme="dark"] .editor-image-scale__input,
      [data-pc-theme="dark"] .editor-image-scale__input {
        background: #1f2a37;
        color: #e2e8f0;
        border-color: rgba(255, 255, 255, 0.22);
      }
      [data-bs-theme="dark"] .editor-image-scale__btn,
      [data-pc-theme="dark"] .editor-image-scale__btn {
        background: transparent;
      }
      [data-bs-theme="dark"] .editor-image-scale__btn:hover:not(:disabled),
      [data-pc-theme="dark"] .editor-image-scale__btn:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.12);
      }
      [data-bs-theme="dark"] .editor-image-scale__input:focus,
      [data-pc-theme="dark"] .editor-image-scale__input:focus {
        border-color: #60a5fa;
        box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.35);
      }
      .react-quill-match-inputs.quill-autogrow .quill {
        display: block;
        height: auto !important;
        min-height: 200px;
      }
      .react-quill-match-inputs.quill-autogrow .ql-container,
      .react-quill-match-inputs.quill-autogrow .ql-container.ql-snow {
        height: auto !important;
      }
      .react-quill-match-inputs.quill-autogrow .ql-editor {
        min-height: 200px;
        height: auto;
        overflow-y: visible;
      }
      /* Editor only. Quill's .ql-editor td { border: 1px solid #000 } stays in the HTML for preview/PDF. */
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-editor td,
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-editor td,
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-editor th,
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-editor th {
        border-color: currentColor !important;
      }
      .react-quill-match-inputs .ql-editor td[style*="border-style: none"],
      .react-quill-match-inputs .ql-editor td[style*="border-style:none"],
      .react-quill-match-inputs .ql-editor td[style*="border-style: hidden"],
      .react-quill-match-inputs .ql-editor td[style*="border-style:hidden"],
      .react-quill-match-inputs .ql-editor td[style*="border: none"],
      .react-quill-match-inputs .ql-editor td[style*="border:none"],
      .react-quill-match-inputs .ql-editor th[style*="border-style: none"],
      .react-quill-match-inputs .ql-editor th[style*="border-style:none"],
      .react-quill-match-inputs .ql-editor th[style*="border-style: hidden"],
      .react-quill-match-inputs .ql-editor th[style*="border-style:hidden"],
      .react-quill-match-inputs .ql-editor th[style*="border: none"],
      .react-quill-match-inputs .ql-editor th[style*="border:none"] {
        border-style: dashed !important;
        border-width: 1px !important;
        border-color: rgba(107, 114, 128, 0.9) !important;
      }
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-editor td[style*="border-style: none"],
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-editor td[style*="border-style: none"],
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-editor td[style*="border-style:none"],
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-editor td[style*="border-style:none"],
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-editor td[style*="border-style: hidden"],
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-editor td[style*="border-style: hidden"],
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-editor td[style*="border-style:hidden"],
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-editor td[style*="border-style:hidden"],
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-editor td[style*="border: none"],
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-editor td[style*="border: none"],
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-editor td[style*="border:none"],
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-editor td[style*="border:none"],
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-editor th[style*="border-style: none"],
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-editor th[style*="border-style: none"],
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-editor th[style*="border-style:none"],
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-editor th[style*="border-style:none"],
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-editor th[style*="border-style: hidden"],
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-editor th[style*="border-style: hidden"],
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-editor th[style*="border-style:hidden"],
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-editor th[style*="border-style:hidden"],
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-editor th[style*="border: none"],
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-editor th[style*="border: none"],
      [data-bs-theme="dark"] .react-quill-match-inputs .ql-editor th[style*="border:none"],
      [data-pc-theme="dark"] .react-quill-match-inputs .ql-editor th[style*="border:none"] {
        border-color: rgba(255, 255, 255, 0.38) !important;
      }
    `}</style>
    <ReactQuillSpinner show={disabled || isBusy}/>
    <ImageScaleField
      getQuill={getSafeEditor}
      enabled={toolbarPreset !== "courseAbout" && !readOnlyEffective}
    /> 
    <ReactQuillWithRef
      ref={handleQuillRef}
      readOnly={readOnlyEffective}
      theme="snow"
      modules={modules}
      formats={formats}
      useSemanticHTML={toolbarPreset === "courseAbout" ? undefined : false}
      value={value}
      onChange={(v:string, _delta:unknown, source:string)=>{
        if (source !== "user") return;
        tableSyncRef.current = { editor: editor ?? tableSyncRef.current.editor, html: v };
        onChange(v)
      }}
    />
    {stickyMode ? (
      <style>{`
        .quill-sticky .ql-toolbar.ql-snow {
          position: sticky;
          top: ${stickyTop}px;
          z-index: 2;
        }
        .ql-table-menus-container {
          z-index: 30 !important;
        }
        .ql-table-dropdown-list,
        .ql-table-dropdown-properties-list,
        .ql-table-properties-form {
          z-index: 40 !important;
        }
      `}</style>
    ) : null}
    </div>
  );
}
